import AttachFileOutlined from '@mui/icons-material/AttachFileOutlined';
import { ReactElement } from 'react';
import { FormattedMessage, unsafeTranslationKey } from 'intl';
import { ReportTranslationKeys } from 'intl/keys';

import {
  AttachmentsScope,
  ImageDisplayStyle,
} from 'shared/domain/report/types/model/common';
import {
  IssueAttachmentsConfigOnView,
  MessageSectionConfigOnView,
  SectionConfigOnView,
} from 'shared/domain/report/types/onView/common';
import {
  ReportContentListItem,
  ReportMessageAttachmentsStyleRow,
  ReportSuboptionRow,
} from '../formUi/presentational';
import { SectionAvailability, sectionUnavailableProps } from '../model';
import { AttachmentsScopeSegmentedControl } from '../segmentedControls/attachmentsScope/presentational';
import { DisplayStyleSegmentedControl } from '../segmentedControls/displayStyle/presentational';
import { ISSUE_REPORT_DISPLAY_STYLES } from './adapters/sectionDisplayStyleOptions';

export function IssueAttachmentsRowPresentational({
  config,
  onConfigChange,
  sectionAvailability,
  onDisplayStyleChange,
  onScopeChange,
  displayStyleOptions = ISSUE_REPORT_DISPLAY_STYLES,
  variant = 'default',
  showIcon = variant === 'default',
}: {
  config: IssueAttachmentsConfigOnView;
  onConfigChange: (config: IssueAttachmentsConfigOnView) => void;
  sectionAvailability: SectionAvailability;
  onDisplayStyleChange: (displayStyle: ImageDisplayStyle) => void;
  onScopeChange: (scope: AttachmentsScope) => void;
  displayStyleOptions?: ImageDisplayStyle[];
  showIcon?: boolean;
  variant?: 'default' | 'nested';
}): ReactElement {
  return (
    <ReportContentListItem
      icon={showIcon ? <AttachFileOutlined /> : undefined}
      variant={variant}
      title={
        <FormattedMessage
          id={ReportTranslationKeys.sectionIssueAttachmentsLabel}
        />
      }
      switchAriaLabelId={ReportTranslationKeys.sectionIssueAttachmentsLabel}
      enabled={config.enabled}
      onEnabledChange={(enabled) =>
        onConfigChange({ ...config, enabled })
      }
      data-qa='report_section_issue_attachments'
      {...sectionUnavailableProps(sectionAvailability)}
    >
      {sectionAvailability.available && config.enabled && (
        <>
          <ReportSuboptionRow
            labelId={ReportTranslationKeys.suboptionDisplayStyle}
          >
            <DisplayStyleSegmentedControl
              value={config.displayStyle._id}
              onChange={onDisplayStyleChange}
              displayStyles={displayStyleOptions}
              data-qa='report_section_issue_attachments_display_style'
            />
          </ReportSuboptionRow>
          <ReportSuboptionRow
            labelId={ReportTranslationKeys.suboptionAttachmentsScope}
          >
            <AttachmentsScopeSegmentedControl
              value={config.scope._id}
              onChange={onScopeChange}
              data-qa='report_section_issue_attachments_scope'
            />
          </ReportSuboptionRow>
        </>
      )}
    </ReportContentListItem>
  );
}

export function SectionRowPresentational({
  icon,
  variant = 'default',
  labelId,
  config,
  onConfigChange,
  sectionAvailability,
  onDisplayStyleChange,
  displayStyleOptions = ISSUE_REPORT_DISPLAY_STYLES,
  'data-qa': dataQa,
}: {
  icon?: ReactElement;
  variant?: 'default' | 'nested';
  labelId: string;
  config: SectionConfigOnView;
  onConfigChange: (config: SectionConfigOnView) => void;
  sectionAvailability: SectionAvailability;
  onDisplayStyleChange: (displayStyle: ImageDisplayStyle) => void;
  displayStyleOptions?: ImageDisplayStyle[];
  'data-qa': string;
}): ReactElement {
  return (
    <ReportContentListItem
      icon={icon}
      variant={variant}
      title={<FormattedMessage id={unsafeTranslationKey(labelId)} />}
      switchAriaLabelId={labelId}
      enabled={config.enabled}
      onEnabledChange={(enabled) => onConfigChange({ ...config, enabled })}
      data-qa={dataQa}
      {...sectionUnavailableProps(sectionAvailability)}
    >
      {sectionAvailability.available && config.enabled && (
        <ReportSuboptionRow labelId={ReportTranslationKeys.suboptionDisplayStyle}>
          <DisplayStyleSegmentedControl
            value={config.displayStyle._id}
            onChange={onDisplayStyleChange}
            displayStyles={displayStyleOptions}
            data-qa={`${dataQa}_display_style`}
          />
        </ReportSuboptionRow>
      )}
    </ReportContentListItem>
  );
}

export function MessageKindRowPresentational({
  icon,
  labelId,
  config,
  onConfigChange,
  sectionAvailability,
  attachmentsAvailability,
  onAttachmentsEnabledChange,
  onDisplayStyleChange,
  // Hide the per-message attachments switch (e.g. decisions, where file
  // attachments are not implemented yet). Defaults to shown.
  showAttachments = true,
  'data-qa': dataQa,
}: {
  icon: ReactElement;
  labelId: string;
  config: MessageSectionConfigOnView;
  onConfigChange: (config: MessageSectionConfigOnView) => void;
  sectionAvailability: SectionAvailability;
  attachmentsAvailability?: SectionAvailability;
  onAttachmentsEnabledChange?: (enabled: boolean) => void;
  onDisplayStyleChange?: (displayStyle: ImageDisplayStyle) => void;
  showAttachments?: boolean;
  'data-qa': string;
}): ReactElement {
  const attachments = config.attachments;
  const attachmentsRow =
    showAttachments &&
    attachmentsAvailability &&
    onAttachmentsEnabledChange &&
    onDisplayStyleChange ? (
      <ReportMessageAttachmentsStyleRow
        attachmentsEnabled={attachments.enabled}
        onAttachmentsEnabledChange={onAttachmentsEnabledChange}
        displayStyle={attachments.displayStyle._id}
        onDisplayStyleChange={onDisplayStyleChange}
        data-qa={dataQa}
        {...sectionUnavailableProps(attachmentsAvailability)}
      />
    ) : undefined;

  return (
    <ReportContentListItem
      icon={icon}
      title={<FormattedMessage id={unsafeTranslationKey(labelId)} />}
      switchAriaLabelId={labelId}
      enabled={config.enabled}
      onEnabledChange={(enabled) => onConfigChange({ ...config, enabled })}
      data-qa={dataQa}
      {...sectionUnavailableProps(sectionAvailability)}
    >
      {attachmentsRow}
    </ReportContentListItem>
  );
}
